import { FeatureFlags } from '../store/types/auth/index';
export type NavigationItem = {
  title: string;
  icon?: string;
  featureFlags?: keyof FeatureFlags | (keyof FeatureFlags)[];
  new?: boolean;
  proOnly?: boolean;
  onlyIfCloud?: boolean;
  visibleForUnlicensed?: boolean;
  hiddenForPro?: boolean;
  featureFlagAxiom?: 'AND' | 'OR';
  callback?: () => void;
} & (
  | { path: string; children?: never }
  | { children: NavigationItem[]; path?: never }
  | { callback: () => void; children?: never; path?: never }
);

export interface NavigationMenu {
  items: NavigationItem[];
  title: string;
  icon: string;
}

export const navigationItemVisible = (
  flagConditions: NavigationItem['featureFlags'],
  featureFlags: FeatureFlags,
  featureFlagAxiom?: 'AND' | 'OR'
): boolean => {
  if (!flagConditions) {
    return true;
  }
  if (typeof flagConditions === 'string') {
    flagConditions = [flagConditions];
  }
  if (featureFlagAxiom === 'AND') {
    return flagConditions.every((flagCondition) => featureFlags[flagCondition]);
  }

  return flagConditions.some((flagCondition) => featureFlags[flagCondition]);
};

export const navigationDrawerConfig: NavigationItem[] = [
  {
    title: 'routes.dashboard',
    path: '/dashboard',
    icon: 'mdi-view-dashboard-outline'
  },
  {
    title: 'routes.inventory',
    icon: 'mdi-router-network-wireless',
    children: [
      { title: 'routes.routers', path: '/devices' },
      { title: 'routes.deviceGroups', path: '/device-groups' },
      { title: 'routes.templates', path: '/templates', proOnly: true }
    ]
  },
  {
    title: 'routes.updateManagement',
    icon: 'mdi-update',
    children: [
      { title: 'routes.firmware', path: '/firmware' },
      {
        title: 'routes.autoUpdateServer',
        path: '/update-server',
        featureFlags: 'autoUpdateServerEnabled'
      },
      {
        title: 'routes.smartUpdate',
        path: '/smart-update',
        onlyIfCloud: true,
        proOnly: true
      },

      { title: 'routes.updateJobs', path: '/update-jobs' }
    ]
  },
  {
    title: 'routes.packageManagement',
    icon: 'mdi-package-variant-closed',
    children: [
      {
        title: 'routes.updatePackages',
        path: '/update-packages',
        proOnly: true
      },
      { title: 'routes.fileStorage', path: '/files', proOnly: true }
    ]
  },
  {
    title: 'routes.certificateManagement',
    icon: 'mdi-shield-key-outline',
    children: [
      {
        title: 'routes.certificate-authorities',
        path: '/certificate-authorities',
        proOnly: true
      },
      { title: 'routes.certificates', path: '/certificates', proOnly: true }
    ]
  },
  {
    title: 'routes.administration',
    icon: 'mdi-account-settings-outline',
    visibleForUnlicensed: true,
    children: [
      {
        title: 'routes.userProfile',
        path: '/profile',
        visibleForUnlicensed: true
      },
      {
        title: 'routes.accountInfo',
        path: '/account',
        visibleForUnlicensed: true
      },
      {
        title: 'routes.users',
        path: '/users',
        onlyIfCloud: true,
        visibleForUnlicensed: true
      },
      {
        title: 'routes.settings',
        path: '/settings',
        visibleForUnlicensed: true
      },
      {
        title: 'routes.licenses',
        path: '/licenses',
        visibleForUnlicensed: true
      }
    ]
  },
  {
    title: 'routes.auditLogs',
    path: '/audit-logs',
    icon: 'mdi-text-box-search-outline'
  },
  {
    title: 'administration.administration',
    icon: 'mdi-wrench-outline',
    featureFlags: ['isAdminAccount', 'isHomeAccount'],
    featureFlagAxiom: 'AND',
    children: [
      {
        title: 'administration.systemInfo',
        path: '/administration/system-information'
      },
      {
        title: 'routes.firmwareManagement',
        path: '/administration/user-and-account-management/firmware-management'
      },
      {
        title: 'administration.accounts',
        path: '/administration/user-and-account-management/accounts',
        featureFlags: ['userManagementEnabled']
      },
      {
        title: 'routes.userManagement',
        path: '/administration/user-and-account-management/users',
        featureFlags: ['userManagementEnabled']
      },
      {
        title: 'administration.organisation',
        path: '/administration/user-and-account-management/organisations',
        featureFlags: ['userManagementEnabled']
      },
      {
        title: 'administration.systemSettings',
        path: '/administration/system-settings'
      }
    ]
  },
  {
    title: 'routes.upgradePlan',
    path: '/plan-overview',
    icon: 'mdi-arrow-up-bold-hexagon-outline',
    hiddenForPro: true,
    visibleForUnlicensed: true,
    onlyIfCloud: true
  }
];

export const bottomNavigationDrawerConfig: NavigationItem[] = [
  {
    title: 'routes.downloadArea',
    path: '/download-area',
    icon: 'mdi-cloud-download-outline',
    onlyIfCloud: true,
    visibleForUnlicensed: true
  },
  {
    title: 'administration.dataPrivacyStatement',
    path: '/data-processing-policy',
    icon: 'mdi-format-section',
    onlyIfCloud: true,
    visibleForUnlicensed: true
  }
];

export const defaultOpenedMenus: string[] = ['routes.inventory'];
